import React, { useState } from 'react';
import { Gift, Copy, Check, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../services/i18n';
import { useReferral } from '../services/referral';
import { ReferralBadge } from './ReferralBadge';

const STATUS = { active: 'bg-emerald-100 text-emerald-800', used: 'bg-gray-100 text-gray-600', expired: 'bg-red-50 text-red-700' };

// Mon compte › Parrainage : how rewarded sharing works, the customer's personal link and the rewards earned so far
export const ReferralSection = () => {
  const { t, lang } = useT();
  const data = useReferral();
  const [copied, setCopied] = useState(false);
  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="referral-loading"><Loader2 size={16} className="animate-spin" /> …</div>;
  const link = `${window.location.origin}/?utm_source=share&utm_medium=copy_link&utm_campaign=referral&ref=${data.code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success(t('product.shareCopied')); setTimeout(() => setCopied(false), 2000); } catch { window.prompt(t('product.shareCopy'), link); }
  };
  const date = (iso) => new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : lang);
  return (
    <div className="space-y-5" data-testid="referral-section">
      <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-5">
        <p className="font-semibold text-gray-900 flex items-center gap-2"><Gift size={18} className="text-orange-500" /> {t('share.refTitle', { p: data.percent })}</p>
        <p className="text-sm text-gray-700 mt-1" data-testid="referral-how">{t('share.refHow', { p: data.percent, d: data.validity_days })}</p>
        {data.enabled === false && <p className="text-xs text-red-700 mt-2" data-testid="referral-disabled">{t('share.refDisabled')}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <code className="text-xs bg-white border border-amber-200 rounded-md px-3 py-2 break-all" data-testid="referral-link">{link}</code>
          <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full bg-orange-500 hover:bg-orange-600 text-white px-3 py-2" data-testid="referral-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {t('product.shareCopy')}</button>
          <a href={`https://wa.me/?text=${encodeURIComponent(`${t('share.refWhatsapp')} ${link}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-green-200 text-green-700 hover:bg-green-50 px-3 py-2" data-testid="referral-whatsapp"><Share2 size={13} /> WhatsApp</a>
        </div>
        <p className="text-xs text-gray-500 mt-2">{t('share.refTip')}</p>
      </div>
      <ReferralBadge rank={data.rank} />
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl shadow-sm p-4" data-testid="referral-total"><p className="text-xs uppercase tracking-wide text-gray-500">{t('share.refTotal')}</p><p className="text-2xl font-bold text-gray-900">{data.total}</p></div>
        <div className="bg-white rounded-xl shadow-sm p-4" data-testid="referral-active"><p className="text-xs uppercase tracking-wide text-gray-500">{t('share.refActive')}</p><p className="text-2xl font-bold text-emerald-700">{data.active}</p></div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-3">{t('share.refRewards')}</h3>
        {data.rewards.length === 0 ? <p className="text-sm text-gray-500" data-testid="referral-empty">{t('share.refEmpty')}</p> : (
          <ul className="divide-y divide-gray-100" data-testid="referral-rewards">
            {data.rewards.map((r) => (
              <li key={r.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`referral-reward-${r.id}`}>
                <code className="font-bold tracking-wider text-orange-700" data-testid={`referral-reward-code-${r.id}`}>{r.code}</code>
                <span className="text-gray-700">−{r.percent} %</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS[r.status]}`} data-testid={`referral-reward-status-${r.id}`}>{t(`share.refStatus_${r.status}`)}</span>
                <span className="text-xs text-gray-500 ml-auto">{t('share.refFriend', { f: r.friend, d: date(r.at) })} · {t('share.refUntil', { d: date(r.ends_at) })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ReferralSection;
